import CameraControls from "camera-controls";
import { Vector3 } from "three";
import { alphaFilter } from "../../lib/misc/util";

import { Context } from "../context";
import { ControllerStrategy } from "./strategy";

export const zoomConfig = {
  dolly: {
    min: 5,
    max: 400,
    speed: 0.15,
    speedTouch: 1,
  },
  fov: {
    min: 0.5,
    max: 10,
    speed: 0.5,
  },
};

export class ZoomDollyControllerStrategy extends ControllerStrategy {
  private configured = false;

  private pointerDownCb = (ev: PointerEvent) => {
    if (this.configured) return;
    if (ev.pointerType == "touch") {
      const controls = this.registered?.controls;
      if (controls) {
        // configure zoom sensitivity for touch
        controls.dollySpeed = zoomConfig.dolly.speedTouch;
      }
    }
    this.configured = true;
  };

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) return this;

    const { controls, domRef } = this.registered;

    if (controls) {
      controls.minDistance = zoomConfig.dolly.min;
      controls.maxDistance = zoomConfig.dolly.max;

      controls.dollySpeed = zoomConfig.dolly.speed;

      controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
      controls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
    }

    domRef.addEventListener("pointerdown", this.pointerDownCb);

    return this;
  }

  teardown() {
    super.teardown();

    if (!this.registered) return this;

    const { controls, domRef } = this.registered;
    if (controls) {
      controls.dollySpeed = 1;
    }
    domRef.removeEventListener("pointerdown", this.pointerDownCb);
  }
}

export class ZoomFOVControllerStrategy extends ControllerStrategy {
  minZoom = zoomConfig.fov.min;
  maxZoom = zoomConfig.fov.max;

  defaultZoom = 1;

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) return this;

    const { controls } = this.registered;

    if (controls) {
      controls.maxZoom = this.maxZoom;
      controls.minZoom = this.minZoom;

      controls.dollySpeed = zoomConfig.fov.speed;

      controls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
      controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;
    }

    // TODO: do we want to assume 1 or get the zoom from the camera?
    // if (camera) {
    //   this.defaultZoom = camera.zoom;
    // }

    return this;
  }

  teardown() {
    super.teardown();

    const controls = this.registered?.controls;
    if (controls) {
      controls.minZoom = 0.01;
      controls.maxZoom = Infinity;

      controls.zoomTo(this.defaultZoom, true);

      controls.dollySpeed = 1;

      controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
      controls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
    }
  }
}

export class AutoZoomControllerStrategy extends ZoomFOVControllerStrategy {
  private pos = new Vector3();
  private target = new Vector3();

  private minDist = zoomConfig.dolly.min;
  private maxDist = zoomConfig.dolly.max;

  private slope = (this.maxZoom - this.minZoom) / (this.maxDist - this.minDist);

  private followOffTimedCb?: () => void;
  private wheelTimeoutMs = 3000;
  private wheelScrollCnt = 0;

  private enabled = true;

  defaultZoom = 1;

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    const { domRef } = this.registered;

    this.enabled = true;

    this.followOffTimedCb = () => {
      if (this.registered) this.enabled = false;
      this.wheelScrollCnt++;

      setTimeout(() => {
        this.wheelScrollCnt = Math.max(this.wheelScrollCnt - 1, 0);
        if (this.wheelScrollCnt == 0 && this.registered?.state.following)
          if (this.registered) this.enabled = true;
      }, this.wheelTimeoutMs);
    };

    domRef?.addEventListener("wheel", this.followOffTimedCb);

    // TODO: do we want to assume 1 or get the zoom from the camera?
    // if (camera) {
    //   this.defaultZoom = camera.zoom;
    // }

    return this;
  }

  update(_deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    if (!this.registered) {
      console.error("no controller registered");
      return;
    }

    if (!this.enabled) return;

    const { contextRef, camera, controls, state } = this.registered;
    const tracked = state.tracked;

    if (!tracked || !contextRef || !controls || !camera) {
      return;
    }

    if (frameNum == undefined || this.lastFrameNum != frameNum) {
      controls.getPosition(this.pos);
      tracked.getWorldPosition(this.target);

      const dist = this.pos.distanceTo(this.target);
      const targetZoom = this.minZoom + this.slope * (dist - this.minDist);

      let zoom = alphaFilter(camera.zoom, targetZoom, 0.1);

      controls.zoomTo(zoom, false);
    }

    this.lastFrameNum = frameNum;
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    const { controls, domRef } = this.registered;

    controls?.zoomTo(this.defaultZoom, true);

    if (this.followOffTimedCb) {
      domRef?.removeEventListener("wheel", this.followOffTimedCb);
    }
  }
}
